import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Calendar, CheckCircle2, Clock, PenLine, Trash2, User, Pencil, Download, Video, MapPin, Link2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDeShort, formatDeShortDateTime } from "@/lib/date";
import { toast } from "sonner";
import AppointmentDialog from "@/components/AppointmentDialog";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect";
import TeamCalendarConnect from "@/components/TeamCalendarConnect";
import GoogleTeamCalendarStatus from "@/components/GoogleTeamCalendarStatus";
import SignaturePad from "@/components/SignaturePad";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const STATUS_LABELS = {
  proposed: { label: "Vorgeschlagen", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { label: "Angenommen", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  rejected: { label: "Abgelehnt", cls: "bg-red-50 text-red-800 border-red-200" },
  completed: { label: "Stattgefunden · warte auf Kunden-Unterschrift", cls: "bg-slate-100 text-slate-800 border-slate-300" },
  signed: { label: "Unterschrieben ✓", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  cancelled_timely: { label: "Rechtzeitig abgesagt", cls: "bg-slate-50 text-slate-700 border-slate-200" },
  cancelled_late: { label: "Kurzfristig abgesagt", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  cancelled_by_coach: { label: "Durch Coach abgesagt", cls: "bg-slate-50 text-slate-700 border-slate-200" },
  cancelled_technical: { label: "Technisch nicht durchführbar", cls: "bg-slate-50 text-slate-700 border-slate-200" },
  no_show: { label: "Nicht erschienen", cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

export default function TerminePage() {
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || (user?.all_roles || []).includes("manager") || (user?.all_roles || []).includes("admin");
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("upcoming");
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [coachSig, setCoachSig] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [pdfClientId, setPdfClientId] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const sigRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get("/appointments"), api.get("/clients")]);
      setAppointments(a.data);
      setClients(c.data);
      if (!pdfClientId && c.data.length > 0) setPdfClientId(c.data[0].client_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return appointments
      .filter((a) => {
        if (clientFilter && a.client_id !== clientFilter) return false;
        if (filter === "upcoming") return a.date >= today && a.status !== "signed" && a.status !== "rejected";
        if (filter === "past") return a.date < today || a.status === "signed";
        return true;
      })
      .sort((a, b) => {
        const c = a.date > b.date ? 1 : a.date < b.date ? -1 : (a.time_from || "").localeCompare(b.time_from || "");
        return filter === "all" ? -c : c;
      });
  }, [appointments, filter, clientFilter]);

  const copySignLink = async (a) => {
    try {
      const { data } = await api.get(`/appointments/${a.appointment_id}/signature-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Unterschrift-Link kopiert — jetzt an den Kunden schicken", { duration: 7000 });
      } catch {
        window.prompt("Unterschrift-Link kopieren:", data.url);
      }
      const txt = encodeURIComponent(`Hallo ${data.client_name || ""}, bitte den Termin am ${data.date} (${data.time_from}–${data.time_to}) hier unterschreiben: ${data.url}`);
      window.open(`https://wa.me/?text=${txt}`, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const isAdmin = user?.role === "admin" || (user?.all_roles || []).includes("admin");

  const requestCoachSignature = async (appts, { replace }) => {
    const list = Array.isArray(appts) ? appts : [appts];
    if (!list.length) { toast.error("Bitte zuerst Termine auswählen"); return; }
    try {
      const { data: coaches } = await api.get("/coaches");
      const avail = (coaches || []).filter((c) => c.name);
      const current = list[0]?.coach_user_id;
      const choice = window.prompt(
        (replace
          ? `Wer hat ${list.length === 1 ? "den Termin" : "diese Termine"} wirklich geleitet? Die bisherige Coach-Unterschrift wird ersetzt.\n\n`
          : `Wer soll ${list.length === 1 ? "den Termin" : "diese Termine"} unterschreiben? Vorhandene Unterschriften bleiben unverändert.\n\n`)
        + list.map((a) => `· ${a.date} ${a.client_name}`).join("\n") + "\n\n"
        + avail.map((c, i) => `${i + 1}. ${c.name}`).join("\n")
        + "\n\nBitte die Nummer eingeben.",
        String(Math.max(1, avail.findIndex((c) => c.user_id === current) + 1)),
      );
      if (!choice) return;
      const coach = avail[Number(choice) - 1];
      if (!coach) { toast.error("Ungültige Auswahl"); return; }
      const { data } = await api.post("/appointments/request-coach-signature", {
        appointment_ids: list.map((a) => a.appointment_id),
        coach_user_id: coach.user_id, send_email: true, replace_existing: !!replace,
      });
      try { await navigator.clipboard.writeText(data.link); } catch { /* egal */ }
      toast.success(
        `${data.count} Termin${data.count === 1 ? "" : "e"} für ${data.coach_name}: `
        + (data.email_sent ? "E-Mail mit Unterschrift-Link verschickt (Link auch kopiert)"
          : "Link kopiert – bitte manuell schicken, es ist keine E-Mail hinterlegt"),
        { duration: 14000 },
      );
      setSelected({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const notifiable = useMemo(() => filtered.filter((a) => ["proposed", "accepted"].includes(a.status)), [filtered]);
  const selectedIds = useMemo(() => notifiable.filter((a) => selected[a.appointment_id]).map((a) => a.appointment_id), [notifiable, selected]);
  const selectedForSignature = useMemo(
    () => filtered.filter((a) => selected[a.appointment_id] && ["completed", "signed"].includes(a.status) && !a.coach_signature_present),
    [filtered, selected],
  );

  const sendForConfirmation = async (ids, { emailOnly = true } = {}) => {
    if (!ids.length) { toast.error("Bitte zuerst Termine auswählen"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/appointments/notify", { appointment_ids: ids, send_email: emailOnly });
      const r = data.results?.[0];
      if (!emailOnly && r) {
        try { await navigator.clipboard.writeText(r.link); } catch { window.prompt("Bestätigungs-Link kopieren:", r.link); }
        toast.success("Bestätigungs-Link kopiert — jetzt an den Kunden schicken", { duration: 8000 });
        const txt = encodeURIComponent(`Hallo ${r.client_name || ""}, bitte bestätige deine Coachingtermine hier: ${r.link}`);
        window.open(`https://wa.me/?text=${txt}`, "_blank", "noopener");
      } else {
        const ok = (data.results || []).filter((x) => x.email_sent);
        const fail = (data.results || []).filter((x) => !x.email_sent);
        if (ok.length) toast.success(`E-Mail mit allen Terminen verschickt an: ${ok.map((x) => x.client_name).join(", ")}`, { duration: 9000 });
        if (fail.length) toast.error(`Keine E-Mail möglich für: ${fail.map((x) => x.client_name).join(", ")} — bitte Link kopieren und manuell schicken`, { duration: 12000 });
      }
      setSelected({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  const openComplete = (a) => {    setCompleteTarget(a);
    setCoachSig(null);
    setCompleteOpen(true);
  };

  const submitComplete = async () => {
    if (!coachSig) { toast.error("Bitte hier unterschreiben"); return; }
    setCompleting(true);
    try {
      await api.put(`/appointments/${completeTarget.appointment_id}/complete`, { coach_signature: coachSig });
      toast.success("Termin markiert & unterschrieben — der Kunde ist an der Reihe");
      setCompleteOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setCompleting(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Termin am ${formatDeShort(a.date)} löschen?`)) return;
    try {
      await api.delete(`/appointments/${a.appointment_id}`);
      toast.success("Termin gelöscht");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadPdf = async () => {
    if (!pdfClientId) { toast.error("Bitte zuerst einen Kunden auswählen"); return; }
    const client = clients.find((c) => c.client_id === pdfClientId);
    try {
      const res = await api.get(`/clients/${pdfClientId}/appointments/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `anwesenheitsliste_${(client?.name || "kunde").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="termine-root">
      <div className="flex items-end justify-between mb-10 border-b border-slate-200 pb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">AVGS</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            Termine
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            Schlage deinen Kunden Termine vor. Nach dem Termin markierst du &quot;Stattgefunden&quot; und unterschreibst
            direkt — der Kunde bekommt in seinem Bereich sofort die Möglichkeit, digital zu unterschreiben.
            Erst dann werden die UE vom Kontingent abgezogen.
          </p>
        </div>
        <button
          data-testid="add-appointment-button"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          disabled={clients.length === 0}
          className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-40"
        >
          <Plus size={16} strokeWidth={1.5} />
          Termin vorschlagen
        </button>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3">
        <GoogleCalendarConnect />
        <span className="text-xs text-slate-500 max-w-md leading-relaxed">
          Verbindest du deinen Google Kalender, werden ALLE deine eigenen AVGS-Termine
          (bestehende und neue) automatisch dorthin übertragen — und du wirst gewarnt, falls
          ein Zeitraum bereits belegt ist. Jeder Coach verbindet dabei nur seinen eigenen Kalender.
        </span>
      </div>

      {isPrivileged && (
        <div className="mb-8">
          <GoogleTeamCalendarStatus />
        </div>
      )}

      {isPrivileged && (
        <div className="mb-8 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-6">
          <TeamCalendarConnect />
          <span className="text-xs text-slate-500 max-w-md leading-relaxed">
            Für's Management: bündelt die Termine ALLER Coaches in einem Kalender (in deinem
            eigenen Google-Konto), jeder Coach farblich unterscheidbar. Dein eigener Google
            Kalender muss dafür oben verbunden sein.
          </span>
        </div>
      )}

      {/* Anwesenheitsliste PDF Export */}
      {clients.length > 0 && (
        <div className="mb-8 bg-white border border-slate-200 p-5 flex flex-wrap items-center gap-4">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <Download size={13} strokeWidth={1.5} /> Anwesenheitsliste PDF
          </div>
          <select
            data-testid="pdf-client-select"
            className="input-tech max-w-xs"
            value={pdfClientId}
            onChange={(e) => setPdfClientId(e.target.value)}
          >
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.name} {c.customer_number ? `· Kd-Nr ${c.customer_number}` : ""}
              </option>
            ))}
          </select>
          <button
            data-testid="download-list-pdf"
            onClick={downloadPdf}
            className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-100 flex items-center gap-2"
          >
            <Download size={13} strokeWidth={1.5} /> Alle Termine als PDF
          </button>
          <span className="text-xs text-slate-500">Eine Liste mit allen stattgefundenen Terminen und Unterschriften.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 border-b border-slate-200 pb-0">
        <div className="flex gap-1">
          {[
            { k: "upcoming", l: "Anstehend" },
            { k: "past", l: "Vergangen · Unterschrieben" },
            { k: "all", l: "Alle" },
          ].map(({ k, l }) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${filter === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              {l}
            </button>
          ))}
        </div>
        {clients.length > 0 && (
          <div className="flex items-center gap-2 pb-2">
            <User size={13} strokeWidth={1.5} className="text-slate-400" />
            <select
              data-testid="termine-client-filter"
              className="input-tech max-w-[240px] text-sm"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            >
              <option value="">Alle Kunden</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.name} {c.customer_number ? `· Kd-Nr ${c.customer_number}` : ""}
                </option>
              ))}
            </select>
            {clientFilter && (
              <button
                data-testid="termine-client-filter-clear"
                onClick={() => setClientFilter("")}
                className="text-xs text-slate-500 hover:text-slate-900 underline"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="border border-slate-200 bg-white p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="termine-notify-bar">
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid="termine-select-all"
              checked={selectedIds.length === notifiable.length && notifiable.length > 0}
              onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(notifiable.map((a) => [a.appointment_id, true])) : {})}
              className="h-4 w-4 accent-slate-900"
            />
            Alle {notifiable.length} offenen Termine auswählen
          </label>
          <div className="text-xs text-slate-500 flex-1 min-w-[200px]">
            Der Kunde bekommt <b>keine</b> automatische E-Mail mehr. Erst wenn du hier sendest, erhält er <b>eine</b> Mail mit allen ausgewählten Terminen.
          </div>
          <button
            data-testid="termine-send-selected"
            disabled={sending || selectedIds.length === 0}
            onClick={() => sendForConfirmation(selectedIds, { emailOnly: true })}
            className="px-4 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-40 flex items-center gap-1.5"
          >
            <Send size={12} strokeWidth={1.5} /> {sending ? "Sendet…" : `${selectedIds.length} Termine an Kunden senden`}
          </button>
          {isAdmin && selectedForSignature.length > 0 && (
            <button
              data-testid="termine-request-coach-signature"
              onClick={() => requestCoachSignature(selectedForSignature, { replace: false })}
              className="px-4 py-2 text-xs font-medium border border-slate-900 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5"
              title="Einen Link an den Coach schicken, der alle ausgewählten leeren Unterschriftsfelder auf einmal abdeckt"
            >
              <PenLine size={12} strokeWidth={1.5} /> {selectedForSignature.length} Coach-Unterschriften anfordern
            </button>
          )}
        </div>
      )}

      {loading && <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Lädt…</div>}      {!loading && filtered.length === 0 && (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <Calendar size={32} strokeWidth={1} className="text-slate-300 mx-auto mb-4" />
          <p className="text-sm text-slate-600">
            {clients.length === 0
              ? "Es sind keine Kunden angelegt. Der Admin muss zuerst Kunden anlegen, bevor du Termine vorschlagen kannst."
              : "Keine Termine in dieser Ansicht."}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((a) => {
          const s = STATUS_LABELS[a.status] || STATUS_LABELS.proposed;
          const ModeIcon = a.mode === "online" ? Video : MapPin;
          const modeLabel = a.mode === "online" ? "Remote" : "Präsenz";
          return (
            <div key={a.appointment_id} className="bg-white border border-slate-200 p-5 flex flex-wrap items-center gap-4" data-testid={`appt-row-${a.appointment_id}`}>
              {(["proposed", "accepted"].includes(a.status) || (isAdmin && ["completed", "signed"].includes(a.status) && !a.coach_signature_present)) && (
                <input
                  type="checkbox"
                  data-testid={`appt-select-${a.appointment_id}`}
                  checked={!!selected[a.appointment_id]}
                  onChange={(e) => setSelected((p) => ({ ...p, [a.appointment_id]: e.target.checked }))}
                  className="h-4 w-4 accent-slate-900 flex-shrink-0"
                  aria-label="Für Sammel-E-Mail auswählen"
                />
              )}
              <div className="flex-shrink-0 w-14 text-center border-r border-slate-100 pr-4">
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{a.date.slice(5, 7)}/{a.date.slice(2, 4)}</div>
                <div className="font-heading text-3xl font-light tracking-tighter text-slate-900">{a.date.slice(8, 10)}</div>
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 text-slate-900">
                  <User size={13} strokeWidth={1.5} className="text-slate-400" />
                  <span className="font-medium">{a.client_name}</span>
                  {a.topic && <span className="text-xs text-slate-500">· {a.topic}</span>}
                </div>
                <div className="mt-1 text-xs text-slate-600 font-mono flex items-center gap-3 flex-wrap">
                  {(a.time_from || a.time_to) && <span className="flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{a.time_from || "?"}–{a.time_to || "?"}</span>}
                  <span>{a.planned_ue} UE</span>
                  <span className="flex items-center gap-1"><ModeIcon size={11} strokeWidth={1.5} />{modeLabel}</span>
                  {a.mode === "online" && (a.meeting_url || a.coach_meet_link) && (
                    <a
                      href={a.meeting_url || a.coach_meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`appt-meet-link-${a.appointment_id}`}
                      className="flex items-center gap-1 text-[#0a788c] hover:text-[#0a96b4] font-medium not-italic"
                      title="Meeting öffnen"
                    >
                      <Video size={11} strokeWidth={1.5} /> Meeting öffnen
                    </a>
                  )}
                  {a.notes && <span className="italic text-slate-500 truncate max-w-[240px]">{a.notes}</span>}
                </div>
                {a.edited_after_signature_by_name && (a.status === "completed" || a.status === "signed") && (
                  <div className="text-[10px] text-amber-700 mt-1" data-testid={`appt-edited-hint-${a.appointment_id}`}>
                    Nachträglich geändert von {a.edited_after_signature_by_name}
                    {a.edited_after_signature_at && ` am ${formatDeShortDateTime(a.edited_after_signature_at)}`}
                  </div>
                )}
                {["completed", "signed"].includes(a.status) && a.coach_sign_token && !a.coach_signature_present && (
                  <div className="text-[10px] text-amber-700 mt-1" data-testid={`appt-coach-sig-pending-${a.appointment_id}`}>
                    Coach-Unterschrift angefordert bei {a.coach_name} – noch nicht unterschrieben
                  </div>
                )}
                {["proposed", "accepted"].includes(a.status) && (
                  <div className={`text-[10px] mt-1 ${a.client_notified_at ? "text-emerald-700" : "text-slate-500"}`} data-testid={`appt-notified-${a.appointment_id}`}>
                    {a.client_notified_at
                      ? `Kunde informiert am ${formatDeShortDateTime(a.client_notified_at)}`
                      : "Noch nicht an den Kunden gesendet"}
                  </div>
                )}
              </div>
              <div className={`px-3 py-1 text-xs font-medium border ${s.cls}`} data-testid={`appt-status-${a.appointment_id}`}>{s.label}</div>
              <div className="flex gap-1 flex-shrink-0">
                {["proposed", "accepted"].includes(a.status) && (
                  <>
                    <button
                      data-testid={`appt-notify-${a.appointment_id}`}
                      onClick={() => sendForConfirmation([a.appointment_id], { emailOnly: true })}
                      disabled={sending}
                      className="px-3 py-2 text-xs border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1"
                      title="Nur diesen Termin per E-Mail zur Bestätigung schicken"
                    ><Send size={12} strokeWidth={1.5} /> Senden</button>
                    <button
                      data-testid={`appt-confirm-link-${a.appointment_id}`}
                      onClick={() => sendForConfirmation([a.appointment_id], { emailOnly: false })}
                      disabled={sending}
                      className="p-2 border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      aria-label="Bestätigungs-Link kopieren"
                      title="Bestätigungs-Link kopieren (ohne E-Mail) – z. B. für WhatsApp"
                    ><Link2 size={12} strokeWidth={1.5} /></button>
                  </>
                )}
                {!["completed", "signed"].includes(a.status) && (
                  <button
                    data-testid={`appt-complete-${a.appointment_id}`}
                    onClick={() => openComplete(a)}
                    className="px-3 py-2 text-xs bg-emerald-700 text-white hover:bg-emerald-800 flex items-center gap-1"
                    title="Auch ohne Bestätigung des Kunden möglich – z. B. wenn der Termin stattgefunden hat"
                  >
                    <CheckCircle2 size={12} strokeWidth={1.5} /> Stattgefunden + Unterschreiben
                  </button>
                )}
                {a.status === "completed" && (
                  <span className="px-3 py-2 text-xs border border-amber-300 bg-amber-50 text-amber-800 flex items-center gap-1">
                    <PenLine size={12} strokeWidth={1.5} /> Warte auf Kunde
                  </span>
                )}
                {a.status === "completed" && (
                  <button
                    data-testid={`appt-sign-link-${a.appointment_id}`}
                    onClick={() => copySignLink(a)}
                    className="px-3 py-2 text-xs border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                    title="Unterschrift-Link kopieren – zum Weitergeben per WhatsApp oder SMS"
                  >
                    <Link2 size={12} strokeWidth={1.5} /> Unterschrift-Link
                  </button>
                )}
                {!["completed", "signed"].includes(a.status) && (
                  <>
                    <button
                      data-testid={`appt-edit-${a.appointment_id}`}
                      onClick={() => { setEditing(a); setDialogOpen(true); }}
                      className="p-2 border border-slate-200 text-slate-600 hover:bg-slate-100"
                      aria-label="Bearbeiten"
                    ><Pencil size={12} strokeWidth={1.5} /></button>
                    {(isPrivileged || (a.created_by || a.coach_user_id) === user?.user_id) && (
                    <button
                      data-testid={`appt-delete-${a.appointment_id}`}
                      onClick={() => remove(a)}
                      className="p-2 border border-slate-200 text-red-600 hover:bg-red-50"
                      aria-label="Löschen"
                    ><Trash2 size={12} strokeWidth={1.5} /></button>
                    )}
                  </>
                )}
                {isAdmin && ["completed", "signed"].includes(a.status) && (
                  !a.coach_signature_present ? (
                    <button
                      data-testid={`appt-ask-coach-sig-${a.appointment_id}`}
                      onClick={() => requestCoachSignature(a, { replace: false })}
                      className="px-3 py-2 text-xs border border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                      title="Coach einen Link schicken, damit sie das leere Unterschriftsfeld selbst unterschreibt – vorhandene Unterschriften bleiben unverändert"
                    >
                      <PenLine size={12} strokeWidth={1.5} /> Unterschrift anfordern
                    </button>
                  ) : (
                    <button
                      data-testid={`appt-fix-coach-sig-${a.appointment_id}`}
                      onClick={() => requestCoachSignature(a, { replace: true })}
                      className="px-3 py-2 text-xs border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-1"
                      title="Falsche Coach-Unterschrift entfernen und den richtigen Coach per Link unterschreiben lassen"
                    >
                      <PenLine size={12} strokeWidth={1.5} /> Coach-Unterschrift korrigieren
                    </button>
                  )
                )}
                {["completed", "signed"].includes(a.status) &&
                  (isPrivileged || (a.created_by || a.coach_user_id) === user?.user_id) && (
                  <>
                    <button
                      data-testid={`appt-edit-${a.appointment_id}`}
                      onClick={() => { setEditing(a); setDialogOpen(true); }}
                      className="p-2 border border-slate-200 text-slate-600 hover:bg-slate-100"
                      aria-label="Bearbeiten"
                      title="UE/Zeit nachträglich ändern"
                    ><Pencil size={12} strokeWidth={1.5} /></button>
                    <button
                      data-testid={`appt-delete-${a.appointment_id}`}
                      onClick={() => remove(a)}
                      className="p-2 border border-slate-200 text-red-600 hover:bg-red-50"
                      aria-label="Löschen"
                      title="Termin löschen"
                    ><Trash2 size={12} strokeWidth={1.5} /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        clients={clients}
        initialData={editing}
        onSaved={load}
      />

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="complete-dialog">
          <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Termin abschließen</div>
            <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
              Stattgefunden bestätigen
            </DialogTitle>
            <DialogDescription className="sr-only">
              Bestätige, dass der Termin stattgefunden hat und unterschreibe als Coach.
            </DialogDescription>
          </DialogHeader>
          <div className="px-8 py-6">
            {completeTarget && (
              <div className="mb-4 text-sm text-slate-700">
                <div className="font-mono text-xs text-slate-500">{completeTarget.client_name} · {formatDeShort(completeTarget.date)}</div>
                <div className="font-medium mt-1">{completeTarget.topic || "Coaching-Termin"}</div>
                <div className="text-xs text-slate-600 mt-1">{completeTarget.planned_ue} UE · {completeTarget.mode === "online" ? "Remote" : "Präsenz"}</div>
              </div>
            )}
            <SignaturePad ref={sigRef} label="Deine Unterschrift (WA)" onChange={setCoachSig} testId="coach-signature-pad" />
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">
              Nach dem Speichern erscheint der Termin im Kunden-Dashboard mit der Aufforderung zur Unterschrift.
              Die UE werden erst nach der Kunden-Unterschrift verbraucht.
            </p>
          </div>
          <DialogFooter className="px-8 py-5 border-t border-slate-100 flex gap-3">
            <button data-testid="complete-cancel" onClick={() => setCompleteOpen(false)} className="px-6 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
              Abbrechen
            </button>
            <button data-testid="complete-submit" onClick={submitComplete} disabled={completing || !coachSig} className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
              {completing ? "Sende…" : "Bestätigen & Signatur senden"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
